'use client';

import type { CerFeatures } from '@slims/shared/data';

import { apiFetch } from '@/lib/utils/api';
import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import type { ScopeTree } from '@/lib/api-types';
import { useAuthStore } from '@/stores/auth-store';

function authKey() {
	const { userId, role, availableOrgs } = useAuthStore.getState();
	return JSON.stringify([userId, role, availableOrgs]);
}

export interface ScopeLeaf {
	features: CerFeatures;
	orgId: string;
	tenantId: string;
	cerId: string;
	cerNome: string;
	/** Nome della configurazione mostrato nei selettori e nelle intestazioni. */
	configNome: string;
	/** Codice GSE dell'area convenzionale, distinto dal nome della configurazione. */
	areaConvenzionale: string | null;
	/** Comune della configurazione. */
	comune: string | null;
	/** Soci non receduti, aggregati dalle anagrafiche. */
	soci: number;
	/** Soci realmente registrati e non receduti, contati sulla tabella `members`. */
	sociAttivi: number;
	/** Configurazione in modalità demo (abilita i pulsanti "Dati demo") */
	demo: boolean;
}

interface ScopeTreeStore {
	tree: ScopeTree;
	leaves: ScopeLeaf[];
	loading: boolean;
	_authKey: string | null;
	fetchTree: () => Promise<void>;
}

function toLeaves(tree: ScopeTree): ScopeLeaf[] {
	return tree.flatMap((cer) =>
		cer.configs.map((cfg) => ({
			features: cfg.features,
			orgId: cfg.id,
			tenantId: cfg.tenantId,
			cerId: cer.id,
			cerNome: cer.nome,
			configNome: cfg.nome,
			areaConvenzionale: cfg.areaConvenzionale,
			comune: cfg.comune,
			soci: cfg.soci,
			sociAttivi: cfg.sociAttivi ?? 0,
			demo: cfg.demo,
		})),
	);
}

// Shared store: the tree is fetched once per session no matter how many
// components consume it (selector, panels, context-org, header…).
const useScopeTreeStore = create<ScopeTreeStore>((set) => ({
	tree: [],
	leaves: [],
	loading: true,
	_authKey: null,
	fetchTree: () => {
		// Una richiesta alla volta: chi chiede mentre ce n'è una in corso per lo
		// stesso utente la riusa, invece di aprirne un'altra (INB-351).
		const requestAuthKey = authKey();
		if (inflight && inflight.authKey === requestAuthKey) return inflight.promise;
		const promise = runFetch(requestAuthKey, set).finally(() => {
			if (inflight?.promise === promise) inflight = null;
		});
		inflight = { authKey: requestAuthKey, promise };
		return promise;
	},
}));

let inflight: { authKey: string; promise: Promise<void> } | null = null;

async function runFetch(
	requestAuthKey: string,
	set: (partial: Partial<ScopeTreeStore>) => void,
): Promise<void> {
	set({ loading: true });
	try {
		const res = await apiFetch('/api/cer/scope-tree');
		if (!res.ok) {
			// A non-ok response (401/403/500) is not fatal but must not be
			// latched as a successful fetch — log it and leave the auth key unset
			// so a later mount can retry once the backend recovers.
			console.error('[USESCOPETREE] scope-tree returned HTTP', res.status);
			return;
		}
		const data = (await res.json()) as { tree: ScopeTree };
		// A childless CER is returned with a phantom config whose id equals the
		// CER's own id. Strip it so such CERs surface as having zero real
		// configurations (and get filtered out of the scope selectors).
		const cleaned = (data.tree ?? []).map((cer) => ({
			...cer,
			configs: cer.configs.filter((cfg) => cfg.id !== cer.id),
		}));
		// Ignore a response from a previous user or an outdated membership set.
		if (requestAuthKey === authKey()) {
			set({ tree: cleaned, leaves: toLeaves(cleaned), _authKey: requestAuthKey });
		}
	} catch (err) {
		// A transient network blip (e.g. the API worker reloading in dev) must
		// not surface as a fatal unhandled rejection, and must not latch the
		// store into a permanent empty tree — leave the auth key unset so
		// the next mount (or a page reload) retries.
		console.error('[USESCOPETREE FETCH ERR]', err);
	} finally {
		if (requestAuthKey === authKey()) set({ loading: false });
	}
}

// In-flight guard shared across all consumers: concurrent mounts reuse the same
// request, but a failed fetch clears it so a later mount can retry (unlike a
// plain boolean flag, which would latch the failure forever).
let treePromise: Promise<void> | null = null;
let pendingAuthKey: string | null = null;
const EMPTY_TREE: ScopeTree = [];

/**
 * Un solo refetch per raffica di eventi realtime (INB-351). Le sottoscrizioni
 * stanno nell'hook, e l'hook lo montano una dozzina di componenti: a ogni
 * modifica di `organizations` (un salvataggio del dettaglio CER) ognuno
 * rilanciava la stessa richiesta — 13 GET a /api/cer/scope-tree in un
 * secondo, ciascuna una subrequest del Worker. Qui gli eventi ravvicinati
 * confluiscono in una richiesta sola.
 */
const REALTIME_REFETCH_DELAY_MS = 300;
let realtimeTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleRealtimeRefetch() {
	if (realtimeTimer) clearTimeout(realtimeTimer);
	realtimeTimer = setTimeout(() => {
		realtimeTimer = null;
		void useScopeTreeStore.getState().fetchTree();
	}, REALTIME_REFETCH_DELAY_MS);
}
const EMPTY_LEAVES: ScopeLeaf[] = [];

interface UseScopeTreeReturn {
	tree: ScopeTree;
	/** Flat list of all reachable configuration leaves. */
	leaves: ScopeLeaf[];
	loading: boolean;
	/** Re-runs the scope-tree fetch. Await it to be sure a just-created CER/config is in the store before navigating. */
	refetch: () => Promise<void>;
}

export function useScopeTree(): UseScopeTreeReturn {
	const currentAuthKey = useAuthStore((s) => JSON.stringify([s.userId, s.role, s.availableOrgs]));
	const fetchedAuthKey = useScopeTreeStore((s) => s._authKey);
	const tree = useScopeTreeStore((s) => s.tree);
	const leaves = useScopeTreeStore((s) => s.leaves);
	const loading = useScopeTreeStore((s) => s.loading);
	const fetchTree = useScopeTreeStore((s) => s.fetchTree);

	// Fetch once per user/membership set across all consumers. A changed role
	// or user invalidates the old tree before any page can use its scopes.
	useEffect(() => {
		const state = useScopeTreeStore.getState();
		if (state._authKey === currentAuthKey || (treePromise && pendingAuthKey === currentAuthKey))
			return;
		pendingAuthKey = currentAuthKey;
		treePromise = state.fetchTree().finally(() => {
			if (pendingAuthKey === currentAuthKey) treePromise = null;
		});
	}, [currentAuthKey]);

	useEffect(
		() =>
			subscribeToTable(
				'cer-features-scope-tree',
				{ event: '*', schema: 'public', table: 'organization_features' },
				scheduleRealtimeRefetch,
			),
		[],
	);

	// Any CER or configuration is a row in `organizations` (parent/child): a
	// superadmin disabling a CER, an admin creating a config, or another tab
	// editing the same tree must all reach this shared store — otherwise the
	// switcher (Soci, Onboarding, header) keeps showing what it saw at the
	// first mount of the session until a hard reload.
	useEffect(() => {
		return subscribeToTable(
			'organizations-scope-tree',
			{ event: '*', schema: 'public', table: 'organizations' },
			scheduleRealtimeRefetch,
		);
	}, []);

	const refetch = useCallback(() => fetchTree(), [fetchTree]);

	const current = fetchedAuthKey === currentAuthKey;
	return {
		tree: current ? tree : EMPTY_TREE,
		leaves: current ? leaves : EMPTY_LEAVES,
		loading,
		refetch,
	};
}
